import { useQueries, useQuery } from '@tanstack/react-query'
import { getIotKeys } from 'common/constants/queryKeys'
import { TTL_5_MIN } from 'common/constants/staleTime'
import { useRef } from 'react'

import { getIotDevice, getIotDevices, getIotSensorHistory, getIotThresholdRangeStatus } from '.'
import { IotHistoryParams, IotSensorHistoryResponse } from './types'

const RANGE_STATUS_BACKOFF_MS = [1000, 5000, 10000, 30000]

export const useIotDevices = (params?: { device_type?: string }) =>
  useQuery({
    queryKey: getIotKeys.all(),
    queryFn: async () => {
      const { data } = await getIotDevices(params)
      return data.data
    },
    staleTime: TTL_5_MIN,
  })

export const useIotDevice = (deviceId: string, options?: { refetchInterval?: number | false }) =>
  useQuery({
    queryKey: getIotKeys.details(deviceId),
    queryFn: async () => {
      const { data } = await getIotDevice(deviceId)
      return data.data
    },
    enabled: !!deviceId,
    staleTime: TTL_5_MIN,
    refetchInterval: options?.refetchInterval,
  })

/**
 * Polls range-status with a backoff (1s, 5s, 10s, then every 30s) while
 * still `pending`, stopping once terminal. No overall time cap — it keeps
 * checking for as long as the page stays open and the status is still
 * `pending`.
 *
 * `forcePoll` keeps polling even when the latest reading isn't `pending` —
 * the read right after a submit can still echo the *previous* cycle's
 * terminal status for a moment (backend hasn't flipped this device to
 * `pending` yet), and without forcing another check the query would settle
 * on that stale reading and never discover the real `pending` state at all.
 * Callers pass true while they're awaiting a submission whose own `pending`
 * reading hasn't been observed yet.
 *
 * TanStack Query v4 re-evaluates `refetchInterval` on every consumer
 * re-render, not just once per actual poll — so the step bookkeeping is
 * driven off `query.state.dataUpdateCount` (which only increments on a real
 * completed fetch), never off how many times this callback itself ran.
 */
export const useIotThresholdRangeStatus = (deviceId: string, forcePoll = false) => {
  // Tracks whether the *previous* evaluation was actively polling, so a
  // transition from stopped -> polling (a fresh cycle) resets the backoff
  // to step 0 instead of picking up wherever an earlier, unrelated cycle
  // left off.
  const cycleActiveRef = useRef(false)
  const cycleStartFetchCountRef = useRef(0)

  return useQuery({
    queryKey: getIotKeys.thresholdRangeStatus(deviceId),
    queryFn: async () => {
      const { data } = await getIotThresholdRangeStatus(deviceId)
      return data.data
    },
    enabled: !!deviceId,
    refetchInterval: (data, query) => {
      const isPending = data?.app_range_sync_status === 'pending'
      const shouldPoll = !!data && (isPending || forcePoll)
      if (!shouldPoll) {
        cycleActiveRef.current = false
        return false
      }

      const fetchCount = query.state.dataUpdateCount
      if (!cycleActiveRef.current) {
        cycleActiveRef.current = true
        cycleStartFetchCountRef.current = fetchCount - 1
      }

      const stepIndex = fetchCount - cycleStartFetchCountRef.current - 1
      return RANGE_STATUS_BACKOFF_MS[
        Math.min(Math.max(stepIndex, 0), RANGE_STATUS_BACKOFF_MS.length - 1)
      ]
    },
  })
}

export const useIotSensorHistories = (
  deviceId: string,
  sensors: string[],
  params: Omit<IotHistoryParams, 'sensor'>
) =>
  useQueries({
    queries: sensors.map((sensor) => ({
      queryKey: getIotKeys.sensorHistory(deviceId, sensor, params as Record<string, unknown>),
      queryFn: async (): Promise<IotSensorHistoryResponse> => {
        const { data } = await getIotSensorHistory(deviceId, { ...params, sensor })
        return data.data
      },
      enabled: !!deviceId && sensors.length > 0,
      staleTime: TTL_5_MIN,
    })),
  })
